import React, { useMemo } from 'react';

import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { Box, ButtonBase } from '@mui/material';

import Typography from '@/components/Typography';
import LoadingIndicator from '@/features/AIChat/AskGXDrawer/components/LoadingIndicator/LoadingIndicator';
import LoadingText from '@/features/AIChat/AskGXDrawer/components/LoadingText';
import RateAnswer from '@/features/AIChat/AskGXDrawer/Messages/RateAnswer';
import type { AIMessage } from '@/features/AIChat/types/chat';
import copyToClipboard from '@/utils/copyToClipboard';

const MessageFooter = ({
  msg,
  isLastMessage,
  isSending,
  version,
  prompt,
  conversationId,
  onRate
}: {
  msg: AIMessage;
  isLastMessage: boolean;
  isSending: boolean;
  version: number;
  prompt: string;
  conversationId: string | null;
  onRate: (id: string, score: -1 | 1) => void;
}) => {
  const response = useMemo(() => {
    return msg.content.map(part => {
      switch (part.kind) {
        case 'text':
          return part.text;
        case 'tool_result':
          if (part.display === 'table') {
            return part.rows;
          }
          return;
        default:
          return;
      }
    });
  }, [msg.content]);

  const textResponse = useMemo(
    () =>
      msg.content
        .map(part => {
          if (part.kind === 'text') {
            return part.text;
          }
        })
        .join(''),
    [msg.content]
  );

  return (
    <Box
      sx={{
        mt: 1,
        display: isLastMessage && !isSending ? 'flex' : undefined,
        alignItems: 'center',
        justifyContent: 'space-between'
      }}
    >
      {isLastMessage && isSending && <LoadingText firstChunkArrived={msg.content.length > 0} />}
      {isLastMessage && <LoadingIndicator animate={isSending} />}
      {((isLastMessage && !isSending) || !isLastMessage) && (
        <Box
          sx={{
            color: 'var(--GX-Text-Disabled, #6C737A)',
            justifySelf: 'flex-end',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: '24px',
              alignSelf: 'flex-end'
            }}
          >
            <ButtonBase disableRipple>
              <ContentCopyOutlinedIcon
                sx={{
                  width: '20px'
                }}
                onClick={() => {
                  void copyToClipboard(textResponse, 'Response copied to clipboard!');
                }}
              />
            </ButtonBase>
            <RateAnswer
              conversationId={conversationId || ''}
              prompt={prompt}
              rating={msg.rating ?? 0}
              response={response}
              version={version}
              onRate={score => {
                onRate(msg.id, score);
              }}
            />
          </Box>
          <Typography
            sx={{
              fontSize: '12px'
            }}
          >
            Please double check responses
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MessageFooter;
